import { CalendarDays, Columns3, List, Plus, UserCheck, Users, Wallet } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { Kpi } from '@/components/common/Blocks'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { fmtMoney } from '../shared/format'
import { useCrmOverview, useCustomers, useDeleteCustomer, useSetStage } from './api'
import { CustomerBoard } from './components/CustomerBoard'
import { CustomerExportMenu } from './components/CustomerExportMenu'
import { CustomerFilters } from './components/CustomerFilters'
import { EMPTY_CUSTOMER_FILTER, type CustomerFilter } from './customerFilter'
import { CustomerFormModal } from './components/CustomerFormModal'
import { CustomerTable, type SortKey } from './components/CustomerTable'
import { Pager } from './components/Pager'
import { FollowUpsCard, FunnelCard } from './components/SideCards'
import { CRM_WRITE_PERM } from './permissions'
import type { Customer, Stage } from './types'
import { toastError, useCrm } from './ui'

const STAGE_RANK: Record<Stage, number> = { lead: 0, demo: 1, proposal: 2, customer: 3, lost: 4 }
const daysAgo = (iso: string) => Math.floor((Date.now() - Date.parse(`${iso}T00:00:00`)) / 86_400_000)

export function CustomersPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const customers = useCustomers()
  const { projects, staffName } = useCrm()
  const setStageM = useSetStage()
  const remove = useDeleteCustomer()
  const [projectId, setProjectId] = useState('prj-orva')
  const overview = useCrmOverview(projectId)
  const [params, setParams] = useSearchParams()
  const [filter, setFilterState] = useState<CustomerFilter>(() => ({ ...EMPTY_CUSTOMER_FILTER, region: params.get('region') ?? 'all' }))
  const { q, stage, owner, region, recency, category } = filter
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'next', dir: 1 })
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(10)
  const [form, setForm] = useState<{ open: boolean; customer: Customer | null }>({ open: false, customer: null })
  const canWrite = can(CRM_WRITE_PERM)
  const view = params.get('view') === 'board' ? 'board' : 'list'
  const setView = (v: string) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p)
        if (v === 'board') n.set('view', 'board')
        else n.delete('view')
        return n
      },
      { replace: true },
    )

  const all = useMemo(
    () => (customers.data ?? []).filter((c) => projectId === 'all' || c.projectId === projectId),
    [customers.data, projectId],
  )
  const regions = [...new Set(all.map((c) => c.region).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'tr'))
  const rows = useMemo(() => {
    const term = q.trim().toLocaleLowerCase('tr')
    const list = all.filter(
      (c) =>
        (stage === 'all' || c.stage === stage) &&
        (owner === 'all' || c.ownerId === owner) &&
        (category === 'all' || (category === 'none' ? !c.categoryId : c.categoryId === category)) &&
        (region === 'all' || c.region === region) &&
        (recency === 'all' ||
          (recency === 'never'
            ? !c.lastMeeting
            : !!c.lastMeeting &&
              (recency === 'older' ? daysAgo(c.lastMeeting) > 30 : daysAgo(c.lastMeeting) <= Number(recency)))) &&
        (!term || `${c.name} ${c.contactName}`.toLocaleLowerCase('tr').includes(term)),
    )
    const val = (c: Customer): string | number => {
      switch (sort.key) {
        case 'stage':
          return STAGE_RANK[c.stage]
        case 'owner':
          return staffName(c.ownerId)
        case 'lastMeeting':
          return c.lastMeeting ?? ''
        case 'next':
          return c.nextAction?.date ?? '9999'
        case 'balance':
          return Number(c.balance)
        default:
          return c.name
      }
    }
    return [...list].sort((a, b) => {
      const x = val(a)
      const y = val(b)
      return (
        (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), 'tr')) * sort.dir
      )
    })
  }, [all, q, stage, owner, region, recency, category, sort, staffName])

  const pages = Math.max(1, Math.ceil(rows.length / size))
  const current = Math.min(page, pages)
  const pageRows = rows.slice((current - 1) * size, current * size)
  const setFilter = (f: CustomerFilter) => {
    setFilterState(f)
    setPage(1)
  }

  const onStage = async (c: Customer, s: Stage) => {
    if (
      !(await confirmModal({
        title: t('crm.stageTitle'),
        description: t('crm.stageBody', { name: c.name, stage: t(`crm.stage.${s}`) }),
        confirmLabel: t('common.confirm'),
      }))
    )
      return
    setStageM.mutate(
      { id: c.id, stage: s },
      { onSuccess: () => toast.success(t('crm.stageChanged')), onError: toastError },
    )
  }
  const onDelete = async (c: Customer) => {
    if (
      !(await confirmModal({
        title: t('crm.deleteTitle'),
        description: t('crm.deleteBody', { name: c.name }),
        confirmLabel: t('common.delete'),
        destructive: true,
      }))
    )
      return
    remove.mutate(c.id, { onSuccess: () => toast.success(t('crm.deleted')), onError: toastError })
  }
  const o = overview.data

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.customers')}
        title={t('crm.list.title')}
        description={t('crm.list.subtitle')}
        actions={
          <>
            <Tabs value={view} onValueChange={setView}>
              <TabsList aria-label={t('crm.list.viewLabel')}>
                <TabsTrigger value="list">
                  <List />
                  {t('crm.list.view.list')}
                </TabsTrigger>
                <TabsTrigger value="board">
                  <Columns3 />
                  {t('crm.list.view.board')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <Select
              className="w-48"
              value={projectId}
              onChange={(v) => {
                setProjectId(v)
                setPage(1)
              }}
              aria-label={t('crm.fields.project')}
              options={[
                { value: 'all', label: t('crm.allProjects') },
                ...projects.map((p) => ({ value: p.id, label: p.name })),
              ]}
            />
            <CustomerExportMenu rows={rows} />
            {canWrite ? (
              <Button onClick={() => setForm({ open: true, customer: null })}>
                <Plus />
                {t('crm.add')}
              </Button>
            ) : null}
          </>
        }
      />

      <div className={cn('grid grid-cols-1 gap-4', view === 'list' && 'xl:grid-cols-[minmax(0,1fr)_20rem]')}>
        <div className="min-w-0 space-y-4">
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <Kpi icon={Users} tone="primary" label={t('crm.stage.lead')} value={o?.counts.lead ?? '—'} />
            <Kpi icon={CalendarDays} tone="info" label={t('crm.kpi.demo')} value={o?.counts.demo ?? '—'} />
            <Kpi icon={UserCheck} tone="success" label={t('crm.kpi.active')} value={o?.counts.customer ?? '—'} />
            <Kpi
              icon={Wallet}
              tone="warning"
              label={t('crm.kpi.pending')}
              value={o ? fmtMoney(o.pendingCollection) : '—'}
            />
          </div>

          <section className="bg-card shadow-card rounded-xl border">
            <CustomerFilters value={filter} onChange={setFilter} regions={regions} />
            {customers.isError ? (
              <ErrorState error={customers.error} onRetry={() => void customers.refetch()} />
            ) : customers.isLoading ? (
              <div className="space-y-2 p-4">
                {Array.from({ length: 6 }, (_, i) => (
                  <Skeleton key={i} className="h-12" />
                ))}
              </div>
            ) : pageRows.length === 0 ? (
              <EmptyState icon={Users} title={all.length ? t('crm.list.noMatch') : t('crm.list.empty')} />
            ) : view === 'board' ? (
              <div className="p-3">
                <CustomerBoard rows={rows} canWrite={canWrite} onStage={(c, s) => void onStage(c, s)} />
              </div>
            ) : (
              <CustomerTable
                rows={pageRows}
                sort={sort}
                onSort={(key) => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : 1 }))}
                canWrite={canWrite}
                onEdit={(customer) => setForm({ open: true, customer })}
                onDelete={(c) => void onDelete(c)}
                onStage={(c, s) => void onStage(c, s)}
              />
            )}
            {view === 'list' ? (
              <Pager
                total={rows.length}
                page={current}
                size={size}
                onPage={setPage}
                onSize={(n) => {
                  setSize(n)
                  setPage(1)
                }}
              />
            ) : null}
          </section>
        </div>
        <aside className={cn('space-y-4', view === 'board' && 'hidden')}>
          <FunnelCard
            overview={o}
            title={t('crm.funnelTitle', {
              project:
                projectId === 'all' ? t('crm.allProjects') : (projects.find((p) => p.id === projectId)?.name ?? ''),
            })}
          />
          <FollowUpsCard overview={o} />
        </aside>
      </div>

      {form.open ? (
        <CustomerFormModal
          key={form.customer?.id ?? 'new'}
          customer={form.customer}
          onDone={() => setForm({ open: false, customer: null })}
        />
      ) : null}
    </>
  )
}
